import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from 'lodash';

import * as actions from "../../../store/actions";

import {
  Paper, Container, Table, TableBody, TableCell, TableFooter, TablePagination, TableRow, TextField, Typography, CircularProgress,
} from '@mui/material';

import EnhancedTableHead from "../../../UI/MaterialTableOverrides/EnhancedTableHead";
import TableActionButtonsLegacy from "../../../UI/MaterialTableOverrides/TableActionButtons-legacy";
import TableActionConfirmButtons from "../../../UI/MaterialTableOverrides/TableActionConfirmButtons";
import TablePaginationActionsWrapped from "../../../UI/MaterialTableOverrides/TablePaginationActions";
import TableTitle from '../../../UI/MaterialTableOverrides/TableTitle';
import { deleteCheckMessage } from "../../../utils/deleteCheckMessage";

export default function TenantFeatureFlags(props) {
  const dispatch = useDispatch();

  const [page, setPage] = useState(1);
  const [alreadySerched, setAlreadySerched] = useState(false);
  const [limit, setLimit] = useState(5);
  const [searchText, setSearchText] = useState("");
  const [orderBy, setOrderBy] = useState("name");
  const [order, setOrder] = useState("asc");
  const [editedIndex, setEditedIndex] = useState(-1);
  const [areaEditText, setFeatureFlagEditText] = useState("");
  const [isAddingNewFeatureFlag, setIsAddingNewFeatureFlag] = useState(false);
  const [actionRow, setActionRow] = useState("");

  const fetchTenant = useCallback(
    () => dispatch(actions.fetchTenantsByIdSuperadmin(props.match.params.id))
  );

  const tenantFeatureFlags = useSelector(state => state.tenants.tenant.tenantFeatureFlags);
  const tenant = useSelector(state => state.tenants.tenant);
  const loadingTenant = useSelector(state => state.tenants.loading);

  const deleteFeatureFlag = (index) => dispatch(actions.deleteFeatureFlags(tenant._id, index))
  const updateFeatureFlag = (index, body) => dispatch(actions.updateFeatureFlags(tenant._id, index, body))
  const postFeatureFlags = (body) => dispatch(actions.postFeatureFlags(tenant._id, body))

  const headRows = [
    { id: "actions", numeric: false, disablePadding: false, label: "Ações" },
    { id: "name", numeric: false, disablePadding: false, label: "Feature Flag" }
  ];

  const handleRequestSort = (event, property) => {
    const isDesc = orderBy === property && order === "desc";
    setOrder(isDesc ? "asc" : "desc");
    setOrderBy(property);
  };

  const editComponent = (index, initialText) => {
    setEditedIndex(index);
    setFeatureFlagEditText(initialText);
    setActionRow('editing');
    setIsAddingNewFeatureFlag(false)
  };

  const cancelEdit = () => {
    setEditedIndex(-1);
  };

  const deleteComponent = (index, objIndex) => {
    setEditedIndex(index);
    setActionRow('deleting');
  };

  const updateComponent = (index) => {
    if (actionRow === 'editing') {
      if (areaEditText === '') return;
      updateFeatureFlag(index, { name: areaEditText }).then((res) => {
        if (res === 'success') {
          setEditedIndex(-1);
          fetchTenant();
        }
      })
    } else if (actionRow === 'deleting') {
      deleteFeatureFlag(index).then((res) => {
        if (res === 'success') {
          setEditedIndex(-1);
          fetchTenant();
        }
      })
    }
  };

  const addNewFeatureFlagRow = () => {
    setIsAddingNewFeatureFlag(!isAddingNewFeatureFlag)
    setEditedIndex(-1);
    setFeatureFlagEditText("");
  }

  const createComponent = () => {
    postFeatureFlags({ name: areaEditText }).then((res) => {
      if (res === 'success') {
        setIsAddingNewFeatureFlag(false)
        fetchTenant();
      }
    })
  }

  const createCancel = () => {
    setIsAddingNewFeatureFlag(false)
  }

  const editFeatureFlag = () => {
    return (
      <TextField
        variant="standard"
        autoFocus
        style={styles.editWidth}
        multiline
        id="area"
        value={areaEditText}
        margin="normal"
        onChange={event => {
          setFeatureFlagEditText(event.target.value.toUpperCase());
        }} />
    );
  };

  const styles = {
    table: {
      position: 'relative',
    },
    opacityContainer: {
      opacity: loadingTenant ? '0.3' : '1',
    },
    spinner: {
      position: 'absolute',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '100%',
      top: '-5px',
      opacity: '1',
      zIndex: 2,
    },
    noResults: {
      height: '100px',
    },
    deleteTextConfirm: {
      marginLeft: '-30px',
      fontWeight: 'bold',
    },
    editWidth: {
      width: '90%'
    }
  }

  return (
    <>
      <Paper>
        <TableTitle
          title='Feature Flags'
          value={searchText}
          onChange={event => {
            setPage(1);
            setSearchText(event.target.value);
          }}
          addRow={() => addNewFeatureFlagRow()}
        />
        <Table style={styles.table}>
          <tbody>
            <tr>
              <td>
                {loadingTenant &&
                  <div style={styles.spinner}>
                    <CircularProgress />
                  </div>}
                {(tenantFeatureFlags && tenantFeatureFlags.length === 0) && !isAddingNewFeatureFlag &&
                  <div style={styles.noResults}>
                    {!loadingTenant && <Typography style={styles.spinner}>Nenhum resultado encontrado</Typography>}
                  </div>}
              </td>
            </tr>
          </tbody>
          <EnhancedTableHead
            style={styles.opacityContainer}
            headRows={headRows}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
          />
          <TableBody style={styles.opacityContainer}>
            {isAddingNewFeatureFlag &&
              <TableRow>
                <TableActionConfirmButtons
                  createComponent={createComponent}
                  createCancel={createCancel}
                />
                <TableCell>{editFeatureFlag()}</TableCell>
              </TableRow>
            }
            {tenantFeatureFlags && tenantFeatureFlags.map((area, index) => (
              <TableRow key={area._id}>
                <TableActionButtonsLegacy
                  objContent={area}
                  deleteComponent={deleteComponent}
                  editComponent={editComponent}
                  updateComponent={updateComponent}
                  cancelEdit={cancelEdit}
                  editedIndex={editedIndex}
                  index={index}
                  action={actionRow}
                  checkDelete={true}
                />
                <TableCell>
                  {index === editedIndex ?
                    actionRow === 'editing' ?
                      editFeatureFlag() :
                      <Typography variant='h6' style={styles.deleteTextConfirm}>
                        Deseja realmente deletar este item?
                      </Typography> :
                    area.name
                  }
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </>
  );

}



